import React, { useState } from 'react';
import { useLocation, NavLink as NavLinkRRD, Link } from 'react-router-dom';
import classnames from 'classnames';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Collapse, NavbarBrand, Navbar, NavItem, NavLink, Nav } from 'reactstrap';
import { Briefcase, PlusCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { getVolumeDealsUrl, getPlusUrl, isSuperAdmin } from '../../utils/helpers';

let vdUrl = '';
let plusUrl = '';

const superAdmin = isSuperAdmin();

function Sidebar({ toggleSidenav, sidenavOpen, routes, logo, rtlActive }) {
  const { t } = useTranslation();

  const [state, setState] = useState({});
  const location = useLocation();

  React.useEffect(() => {
    vdUrl = getVolumeDealsUrl();
    plusUrl = getPlusUrl();

    setState(getCollapseStates(routes));

    // eslint-disable-next-line
  }, []);

  // verifies if routeName is the one active (in browser input)
  const activeRoute = (routeName) => (location.pathname.indexOf(routeName) > -1 ? 'active' : '');

  // makes the sidenav normal on hover (actually when mouse enters on it)
  const onMouseEnterSidenav = () => {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      document.body.classList.add('g-sidenav-show');
    }
  };

  // makes the sidenav mini on hover (actually when mouse leaves from it)
  const onMouseLeaveSidenav = () => {
    if (!document.body.classList.contains('g-sidenav-pinned')) {
      document.body.classList.remove('g-sidenav-show');
    }
  };

  // this creates the intial state of this component based on the collapse routes
  // that it gets through routes
  const getCollapseStates = (rts) => {
    let initialState = {};
    rts.map((prop) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState
        };
      }
      return null;
    });
    return initialState;
  };

  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = (rts) => {
    for (let i = 0; i < rts.length; i++) {
      if (rts[i].collapse && getCollapseInitialState(rts[i].views)) {
        return true;
      }
      if (location.pathname.indexOf(rts[i].path) !== -1) {
        return true;
      }
    }
    return false;
  };

  // this is used on mobile devices, when a user navigates
  // the sidebar will autoclose
  const closeSidenav = () => {
    if (window.innerWidth < 1200) {
      toggleSidenav();
    }
  };

  // this function creates the links and collapses that appear in the sidebar (left menu)
  const createLinks = (rts) =>
    rts.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      if (prop.collapse) {
        const st = {};
        st[prop.state] = !state[prop.state];
        return (
          // eslint-disable-next-line react/no-array-index-key
          <NavItem key={key}>
            <NavLink
              href="#pablo"
              data-toggle="collapse"
              aria-expanded={state[prop.state]}
              className={classnames({
                active: getCollapseInitialState(prop.views)
              })}
              onClick={(e) => {
                e.preventDefault();
                setState(st);
              }}
            >
              {prop.icon ? (
                <>
                  <i className="ni text-default">{prop.icon}</i>
                  <span className="nav-link-text">
                    {t(`sidebar.${prop.path.replace('/', '')}`, prop.name)}
                  </span>
                </>
              ) : prop.miniName ? (
                <>
                  <span className="sidenav-mini-icon"> {prop.miniName} </span>
                  <span className="sidenav-normal">
                    {t(`sidebar.${prop.path.replace('/', '')}`, prop.name)}
                  </span>
                </>
              ) : null}
            </NavLink>
            <Collapse isOpen={state[prop.state]}>
              <Nav className="nav-sm flex-column">{createLinks(prop.views)}</Nav>
            </Collapse>
          </NavItem>
        );
      }
      if (prop.noMenu) return null;
      if (prop.onlySuperAdmin && !superAdmin) return null;
      return (
        // eslint-disable-next-line react/no-array-index-key
        <NavItem className={activeRoute(prop.layout + prop.path)} key={key}>
          <NavLink to={prop.layout + prop.path} onClick={closeSidenav} tag={NavLinkRRD}>
            {prop.icon !== undefined ? (
              <>
                <i className="ni text-default">{prop.icon}</i>
                <span className="nav-link-text">
                  {t(`sidebar.${prop.path.replace('/', '')}`, prop.name)}
                </span>
              </>
            ) : prop.miniName !== undefined ? (
              <>
                <span className="sidenav-mini-icon"> {prop.miniName} </span>
                <span className="sidenav-normal">
                  {' '}
                  {t(`sidebar.${prop.path.replace('/', '')}`, prop.name)}{' '}
                </span>
              </>
            ) : (
              t(`sidebar.${prop.path.replace('/', '')}`, prop.name)
            )}
          </NavLink>
        </NavItem>
      );
    });

  let navbarBrandProps;
  if (logo && logo.innerLink) {
    navbarBrandProps = {
      to: logo.innerLink,
      tag: Link
    };
  } else if (logo && logo.outterLink) {
    navbarBrandProps = {
      href: logo.outterLink,
      target: '_blank'
    };
  }
  const scrollBarInner = (
    <div className="scrollbar-inner">
      <div className="sidenav-header d-flex align-items-center">
        {/* {logo ? ( */}
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <NavbarBrand {...navbarBrandProps} className="p-0 pt-2" style={{ flex: 1 }}>
          <div className="text-center">
            <h1 className="font-weight-bolder" style={{ fontSize: '30px' }}>
              GOAT
            </h1>
          </div>
        </NavbarBrand>
        {/* ) : null} */}
        <div className="ml-auto">
          <div
            className={classnames('sidenav-toggler d-none d-xl-block', {
              active: sidenavOpen
            })}
            onClick={toggleSidenav}
          >
            <div className="sidenav-toggler-inner">
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
              <i className="sidenav-toggler-line" />
            </div>
          </div>
        </div>
      </div>
      <div className="navbar-inner">
        <Collapse navbar isOpen>
          <Nav navbar>{createLinks(routes)}</Nav>
          <hr className="my-3" />
          <h6 className="navbar-heading p-0 text-muted">
            <span className="docs-normal">{t('sidebar.other-links', 'Övriga länkar')}</span>
          </h6>
          <Nav className="mb-md-3" navbar>
            <NavItem>
              <NavLink
                // eslint-disable-next-line max-len
                href={plusUrl}
                target="_blank"
              >
                <i className="ni text-green">
                  <PlusCircle size={16} />
                </i>
                <span className="nav-link-text"> PLUS</span>
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink href={vdUrl} target="_blank">
                <i className="ni text-green">
                  <Briefcase size={16} />
                </i>
                <span className="nav-link-text">VolumeDeals</span>
              </NavLink>
            </NavItem>
            {/* <NavItem>
              <NavLink
                href="https://demos.creative-tim.com/argon-dashboard-pro-react/#/documentation/alert?ref=adpr-sidebar"
                target="_blank"
              >
                <i className="ni ni-ui-04" />
                <span className="nav-link-text">Er Golfamore sida</span>
              </NavLink>
            </NavItem> */}
          </Nav>
        </Collapse>
      </div>
    </div>
  );
  return (
    <Navbar
      className={`sidenav navbar-vertical navbar-expand-xs navbar-light bg-white ${
        rtlActive ? '' : 'fixed-left'
      }`}
      onMouseEnter={onMouseEnterSidenav}
      onMouseLeave={onMouseLeaveSidenav}
    >
      {navigator.platform.indexOf('Win') > -1 ? (
        <PerfectScrollbar>{scrollBarInner}</PerfectScrollbar>
      ) : (
        scrollBarInner
      )}
    </Navbar>
  );
}

export default Sidebar;
